import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@tanium/react-box';
import { Shimmer } from '@tanium/react-shimmer';
import { Button } from '@tanium/react-button';
import { Stack } from '@tanium/react-stack';
import { Notification } from '@tanium/react-persistent-notifications';
import { FormikProps } from 'formik';
import { AsyncSuspense } from '../AsyncSuspense';
import { AsyncStatus, AsyncState } from '../../hooks/useAsync';
import { ConfigureIdpRequest } from '../../models/IdpModels';
import { parseApiErrorMsg, ApiError } from '../../hooks/useApiErrorMsgParser';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';

interface IdpSetupTestProps {
  applicationResponse: AsyncState<void, () => Promise<void>, ApiError>;
  formData: FormikProps<ConfigureIdpRequest>;
  idpId: string | undefined;
  onApplyChanges: () => void;
  onClose: () => void;
}

export const IdpSetupTest: React.VFC<IdpSetupTestProps> = ({
  applicationResponse,
  formData,
  idpId: _idpId,
  onApplyChanges,
  onClose,
}) => {
  const { t } = useTranslation();
  const [validationError, setValidationError] = useState('');
  const updateIdpConfigInProgress = applicationResponse.status === AsyncStatus.Pending;
  const { createIdp, updateIdp } = useIdentityProviderConfigs();

  const isFormTouched = formData.dirty;
  // TODO(AMP-434): enable "Test IDP" button to Create/Update IDP form
  // const disableTestBtn = idpId === undefined || formData.dirty;
  // const portalUrl = window.location.origin;
  // const onTestSetup = useCallback(() => {
  //   const consoleConfirmLoginPageWidth = Math.min(1000, window.innerWidth);

  //   const consoleConfirmLoginPageLeft =
  //     window.screenLeft + Math.floor(window.innerWidth / 2 - consoleConfirmLoginPageWidth / 2);
  //   window.open(
  //     `${portalUrl}/confirm-idp-login/`,
  //     '_blank',
  //     `width=${consoleConfirmLoginPageWidth},height=700,top=100,left=${consoleConfirmLoginPageLeft},resizable=0,location=0,toolbar=0,menubar=0,scrollbars=1`
  //   );
  // }, [portalUrl]);

  useEffect(() => {
    const createOrUpdateError = createIdp.error ?? updateIdp.error;
    if (createOrUpdateError?.message) {
      setValidationError(createOrUpdateError.message);
    }
  }, [createIdp.error, updateIdp.error]);

  const handleOnApplyChanges = useCallback(async () => {
    applicationResponse.reset();
    const errors = await formData.validateForm();
    if (Object.values(errors).length > 0) {
      setValidationError(Object.values(errors)[0] as string);
      return;
    }
    setValidationError('');
    onApplyChanges();
  }, [applicationResponse, formData, onApplyChanges]);

  const submissionError = useMemo(() => {
    if (!applicationResponse.error) return '';
    return parseApiErrorMsg(t, applicationResponse.error);
  }, [applicationResponse.error, t]);

  return (
    <Box>
      <Stack direction="column" itemSpacing={1}>
        <Stack direction="row" itemSpacing={2}>
          <Button onClick={handleOnApplyChanges} disabled={!isFormTouched || updateIdpConfigInProgress}>
            {t('Apply Changes')}
          </Button>
          {/* TODO(AMP-434): enable "Test IDP" button to Create/Update IDP form
          <Button onClick={onTestSetup} disabled={disableTestBtn}>
            {t('Test IDP')}
          </Button>
          */}
          <Button onClick={onClose} disabled={updateIdpConfigInProgress} variant="secondary">
            {t('Close')}
          </Button>
        </Stack>
        <Box>
          {validationError && (
            <Notification role="alert" type="error">
              {validationError}
            </Notification>
          )}
        </Box>
        <AsyncSuspense state={applicationResponse} loading={<Shimmer />} failure={submissionError}>
          {applicationResponse.status === AsyncStatus.Success ? (
            <Notification role="alert" type="success">
              {t('The identity provider has been updated')}
            </Notification>
          ) : (
            <></>
          )}
        </AsyncSuspense>
      </Stack>
    </Box>
  );
};
